import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getOrderClaimForm, getOrderClaimStatus, sendOrderClaimRequestSaga } from 'domains/order/Order.store';
import {
  isHidden,
  isMandatory,
  useIsClaimedQuantityComputed,
  useIsClaimFormValid,
} from 'pages/MyOrdersPage/OrderClaims/claimForm.utils';
import { makeKey, RULES_DATA } from 'pages/MyOrdersPage/OrderClaims/claimFormRulesData';
import AttachmentsSection from 'pages/MyOrdersPage/OrderClaims/FormSections/AttachmentsSection';
import ClaimFormSubmitButton from 'pages/MyOrdersPage/OrderClaims/FormSections/ClaimFormSubmitButton';
import ContactSection from 'pages/MyOrdersPage/OrderClaims/FormSections/ContactSection';
import FormHeader from 'pages/MyOrdersPage/OrderClaims/FormSections/FormHeader';
import LabourSection from 'pages/MyOrdersPage/OrderClaims/FormSections/LabourSection';
import MessageSection from 'pages/MyOrdersPage/OrderClaims/FormSections/MessageSection';
import OtherSection from 'pages/MyOrdersPage/OrderClaims/FormSections/OtherSection';
import ReasonSection from 'pages/MyOrdersPage/OrderClaims/FormSections/ReasonSection';
import SymptomSection from 'pages/MyOrdersPage/OrderClaims/FormSections/SymptomSection';
import VehicleSection from 'pages/MyOrdersPage/OrderClaims/FormSections/VehicleSection';
import { Flex, Form, Modal } from 'UI';
import { LOADING } from 'utils';

interface ClaimFormProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const ClaimFormModal = ({ isOpen, setIsOpen }: ClaimFormProps) => {
  const dispatch = useDispatch();
  const claimStatus = useSelector(getOrderClaimStatus);
  const claimForm = useSelector(getOrderClaimForm);
  const [claimSent, setClaimSent] = useState(false);
  const [isQuantityClaimedTouched, setIsQuantityClaimedTouched] = useState(false);
  const baseClaimFormValid = useIsClaimFormValid();
  const isClaimedQuantityComputed = useIsClaimedQuantityComputed();

  const rulesData =
    RULES_DATA[makeKey({ formType: claimForm.formType, reason: claimForm.reason, option: claimForm.option })];

  const requiresManualQuantityClaimed =
    !!rulesData &&
    isMandatory(rulesData.quantityClaimed) &&
    !isHidden(rulesData.quantityClaimed) &&
    !isClaimedQuantityComputed;

  const isClaimFormValid =
    baseClaimFormValid &&
    !(requiresManualQuantityClaimed && claimForm.quantityClaimed === 0 && !isQuantityClaimedTouched);

  const handleSendClaim = () => {
    if (isClaimFormValid && claimStatus !== LOADING) {
      dispatch(sendOrderClaimRequestSaga());
      setClaimSent(true);
    }
  };

  useEffect(() => {
    if (claimStatus === undefined && claimSent) {
      setIsOpen(false);
      setClaimSent(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimStatus]);

  useEffect(() => {
    setIsQuantityClaimedTouched(false);
  }, [claimForm.formType, claimForm.reason, claimForm.option]);

  return (
    <Modal open={isOpen} onCancel={() => setIsOpen(false)} zIndex={1000} footer={''} width={1200}>
      <Form onFinish={handleSendClaim}>
        <Flex direction={'column'} gap={15} minHeight={600}>
          <FormHeader />
          <ReasonSection />
          <ContactSection />
          <OtherSection onQuantityClaimedTouchedChange={setIsQuantityClaimedTouched} />
          <VehicleSection />
          <LabourSection />
          <SymptomSection />
          <MessageSection />
          <AttachmentsSection />
          <ClaimFormSubmitButton claimStatus={claimStatus} isClaimFormValid={isClaimFormValid} />
        </Flex>
      </Form>
    </Modal>
  );
};

export default ClaimFormModal;
